import { ChartContainer, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/shadcn/charts'
import { normalizeRechartsData } from '../shared/rechartsDataUtils'
import { Cell, LabelList, Pie, PieChart } from 'recharts'
import { ChartPieConfig, ChartPieDataItem } from './types'

const percentageFormatter = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 2,
})

export function PieChartBody({ chartConfig, data }: { chartConfig: ChartPieConfig; data: ChartPieDataItem[] }) {
  const { config } = chartConfig
  const { data: chartData, mapDataKey } = normalizeRechartsData(data, [chartConfig.dataKey, chartConfig.categoryKey])
  const normalizedDataKey = mapDataKey(chartConfig.dataKey)
  const normalizedCategoryKey = mapDataKey(chartConfig.categoryKey)
  const total = chartData.reduce((sum, item) => {
    const value = Number(item[normalizedDataKey])
    return Number.isFinite(value) ? sum + value : sum
  }, 0)
  const formatTooltipValue = (value: unknown): string | number | undefined => {
    const numericValue = Number(value)
    if (chartConfig.valueFormat === 'percentage' && total !== 0 && Number.isFinite(numericValue)) {
      return percentageFormatter.format(numericValue / total)
    }
    if (typeof value === 'number') {
      return value.toLocaleString()
    }
    return typeof value === 'string' ? value : undefined
  }
  const legendItems = Array.from(new Set(chartData.map((item) => String(item[normalizedCategoryKey])))).map((key) => ({
    key,
    label: config[key]?.label ?? key,
    color: config[key]?.color,
    icon: config[key]?.icon,
  }))

  return (
    <ChartContainer
      className="mx-auto h-full w-full"
      config={config}
      legend={chartConfig.showLegend !== false ? <ChartLegendContent items={legendItems} /> : undefined}
    >
      <PieChart>
        <ChartTooltip
          content={
            <ChartTooltipContent
              valueFormatter={chartConfig.valueFormat === 'percentage' ? formatTooltipValue : undefined}
            />
          }
        />
        <Pie
          data={chartData}
          dataKey={normalizedDataKey}
          innerRadius={chartConfig.donutRadius ? `${chartConfig.donutRadius}%` : undefined}
          label={chartConfig.showDataLabels ? { fill: '#000' } : false}
          labelLine={{ stroke: '#000' }}
          nameKey={normalizedCategoryKey}
        >
          {chartConfig.showCategoryLabels && (
            <LabelList
              dataKey={normalizedCategoryKey}
              className="fill-offwhite"
              stroke="none"
              fontSize={12}
              formatter={(value: keyof typeof config) => config[value]?.label}
            />
          )}
          {chartData.map((item) => (
            <Cell
              key={item[normalizedCategoryKey]}
              fill={config[item[normalizedCategoryKey] as keyof typeof config]?.color}
            />
          ))}
        </Pie>
      </PieChart>
    </ChartContainer>
  )
}
